import { ReactNode, useEffect, useRef } from "react";

interface Props {
  items: ReactNode[];
  /** seconds for one full loop */
  speed?: number;
  /** left = default; right reverses direction */
  direction?: "left" | "right";
  className?: string;
  itemClassName?: string;
  /** separator between text items (ignored for image marquees) */
  separator?: ReactNode;
  /** allow user to horizontally scroll/drag; pauses animation on interaction */
  scrollable?: boolean;
}

/**
 * Seamless marquee.
 * - Default: pure-CSS translate animation (pauses on hover via index.css).
 * - Scrollable mode: JS-driven native scrollLeft auto-advance so user drag and
 *   auto-scroll share the same coordinate system (no drift, can always scroll
 *   back to the true start, no blank space at the "end").
 */
export const MarqueeStrip = ({
  items,
  speed = 45,
  direction = "left",
  className = "",
  itemClassName = "",
  separator,
  scrollable = false,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstTrackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollable) return;
    const container = containerRef.current;
    const firstTrack = firstTrackRef.current;
    if (!container || !firstTrack) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    let trackWidth = firstTrack.getBoundingClientRect().width;
    const ro = new ResizeObserver(() => {
      trackWidth = firstTrack.getBoundingClientRect().width;
    });
    ro.observe(firstTrack);

    // Start near the middle of the first track so the user can also scroll
    // backwards a full track width before hitting the (wrapped) start.
    // Actually keep at 0 so "scroll back to start" reaches the true first item.
    container.scrollLeft = 0;

    let pausedUntil = 0;
    let rafId = 0;
    let lastTs = performance.now();

    const bumpPause = () => {
      pausedUntil = performance.now() + 1200;
    };

    const tick = (ts: number) => {
      const dt = ts - lastTs;
      lastTs = ts;
      if (!prefersReducedMotion && ts >= pausedUntil && trackWidth > 0) {
        const pxPerMs = trackWidth / (speed * 1000);
        const delta = pxPerMs * dt;
        let next =
          direction === "right"
            ? container.scrollLeft - delta
            : container.scrollLeft + delta;
        if (next >= trackWidth) next -= trackWidth;
        if (next < 0) next += trackWidth;
        container.scrollLeft = next;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    const onEnter = () => bumpPause();
    const onDown = () => bumpPause();
    const onWheel = () => bumpPause();
    const onScroll = () => {
      // While paused, seamless-wrap if user drags past bounds.
      if (performance.now() < pausedUntil && trackWidth > 0) {
        if (container.scrollLeft >= trackWidth) container.scrollLeft -= trackWidth;
        else if (container.scrollLeft < 0) container.scrollLeft += trackWidth;
      }
    };

    container.addEventListener("mouseenter", onEnter);
    container.addEventListener("pointerdown", onDown);
    container.addEventListener("touchstart", onDown, { passive: true });
    container.addEventListener("wheel", onWheel, { passive: true });
    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      container.removeEventListener("mouseenter", onEnter);
      container.removeEventListener("pointerdown", onDown);
      container.removeEventListener("touchstart", onDown);
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("scroll", onScroll);
    };
  }, [scrollable, speed, direction, items.length]);

  const trackContent = items.map((it, i) => (
    <div key={i} className={`flex items-center gap-10 ${itemClassName}`}>
      {it}
      {separator && <span aria-hidden>{separator}</span>}
    </div>
  ));

  if (scrollable) {
    return (
      <div
        ref={containerRef}
        className={`ed-marquee relative overflow-x-auto overflow-y-hidden ed-marquee-scroll ${className}`}
        aria-hidden="true"
      >
        <div className="flex w-max">
          <div ref={firstTrackRef} className="flex items-center shrink-0 gap-10 pr-10">
            {trackContent}
          </div>
          <div className="flex items-center shrink-0 gap-10 pr-10" aria-hidden="true">
            {trackContent}
          </div>
        </div>
      </div>
    );
  }

  const track = (
    <div
      className="ed-marquee-track flex items-center shrink-0 gap-10 pr-10"
      style={{
        animationDuration: `${speed}s`,
        animationDirection: direction === "right" ? "reverse" : "normal",
      }}
    >
      {trackContent}
    </div>
  );

  return (
    <div
      className={`ed-marquee relative overflow-hidden ${className}`}
      aria-hidden="true"
    >
      <div className="flex w-max ed-marquee-viewport">
        {track}
        {track}
      </div>
    </div>
  );
};

export default MarqueeStrip;
